import classNames from 'classnames';
import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';

// Each capture format: its accent colours and inline glyph. Title/description
// come from i18n (`pages.home.section18.<id>Title` / `<id>Desc`).
interface FormatConfig {
  id: string;
  background: string;
  color: string;
  icon: ReactNode;
}

const FORMATS: FormatConfig[] = [
  {
    id: 'quotes',
    background: 'rgba(255,233,240,0.14)',
    color: '#f5a3c2',
    icon: (
      <>
        <path d="M7 7h6M7 11h10" />
        <path d="M3 4h18v13H8l-5 4z" />
      </>
    ),
  },
  {
    id: 'links',
    background: 'rgba(189,222,255,0.14)',
    color: '#9cc0ff',
    icon: (
      <>
        <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
        <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
      </>
    ),
  },
  {
    id: 'photos',
    background: 'rgba(253,246,187,0.14)',
    color: '#FDE47A',
    icon: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="2.5" />
        <circle cx="8.5" cy="9.5" r="1.6" />
        <path d="M21 16l-5-5L4 20" />
      </>
    ),
  },
  {
    id: 'videos',
    background: 'rgba(200,190,255,0.16)',
    color: '#b9a8ff',
    icon: (
      <>
        <rect x="3" y="5" width="14" height="14" rx="2.5" />
        <path d="M17 9l4-2v10l-4-2" />
      </>
    ),
  },
  {
    id: 'voiceNotes',
    background: 'rgba(209,242,221,0.16)',
    color: '#7fd4a3',
    icon: (
      <>
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
      </>
    ),
  },
  {
    id: 'lists',
    background: 'rgba(189,222,255,0.14)',
    color: '#9cc0ff',
    icon: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />,
  },
  {
    id: 'ideas',
    background: 'rgba(253,246,187,0.14)',
    color: '#FDE47A',
    icon: (
      <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2V18h6v-1.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2zM9 22h6" />
    ),
  },
  {
    id: 'notesDocs',
    background: 'rgba(255,233,240,0.14)',
    color: '#f5a3c2',
    icon: (
      <>
        <path d="M4 4h11l5 5v11a0 0 0 0 1 0 0H4z" />
        <path d="M15 4v5h5M8 13h8M8 17h5" />
      </>
    ),
  },
];

export const Section18 = () => {
  const t = useTranslations();

  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.head}>
          <div className={styles.eyebrow}>
            {t('pages.home.section18.eyebrow')}
          </div>
          <h2 className={styles.title}>
            {t.rich('pages.home.section18.title', {
              highlight: (chunks) => (
                <span className={styles.emphasis}>{chunks}</span>
              ),
            })}
          </h2>
          <p className={styles.lead}>{t('pages.home.section18.lead')}</p>
        </div>

        <div className={styles.grid}>
          {FORMATS.map((format) => (
            <div key={format.id} className={styles.card}>
              <div
                className={styles.icon}
                style={{ background: format.background, color: format.color }}
              >
                <svg viewBox="0 0 24 24" className={styles.glyph}>
                  {format.icon}
                </svg>
              </div>
              <h3 className={styles.cardTitle}>
                {t(`pages.home.section18.${format.id}Title`)}
              </h3>
              <p className={styles.cardDesc}>
                {t(`pages.home.section18.${format.id}Desc`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy py-[72px] text-center',
  wrap: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  head: 'mx-auto mb-12 max-w-[660px]',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'mx-auto text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-4xl lg:text-[40px]',
  emphasis: 'text-mf-blue marketing-dark:text-mf-coral',
  lead: 'mx-auto mt-4 max-w-[560px] text-lg leading-[1.65] text-mf-gray-600 marketing-dark:text-white/66',
  grid: 'grid grid-cols-1 gap-[18px] text-left sm:grid-cols-2 lg:grid-cols-4',
  // Flat grid cards: hairline border in light, translucent tint on navy.
  card: classNames(
    'rounded-[18px] border px-[22px] pb-5 pt-[22px] transition-all duration-200 hover:-translate-y-1',
    'border-[#F0F0F0] bg-white hover:border-[#DADADA]',
    'marketing-dark:border-white/[0.09] marketing-dark:bg-white/[0.04]',
    'marketing-dark:hover:border-white/20 marketing-dark:hover:bg-white/[0.07]',
  ),
  icon: 'mb-4 flex h-11 w-11 items-center justify-center rounded-[13px]',
  glyph: 'h-[22px] w-[22px] fill-none stroke-current stroke-[1.75]',
  cardTitle:
    'mb-1.5 font-inter text-[16.5px] font-bold text-mf-off-black-600 marketing-dark:text-white',
  cardDesc:
    'text-[13.5px] leading-[1.55] text-mf-gray-600 marketing-dark:text-white/55',
};
